import { useMemo, useState } from 'react';
import { useLoaderData } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { LoadMoreButton } from '~/ui-rocco/pages/detail-shared/LoadMoreButton';
import { useItemAdminData } from '~/modules/directory/hook/use-item-admin-data';
import type { loader } from '~/routes/_app.product.$urlSegment';
import {
    DetailTileCard,
    useIslandSection,
    useMagicIslandController,
    type DetailTile,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';
import { useEnquiryOwnsIsland } from '../../hooks/use-enquiry-owns-island';
import {
    PRODUCT_TILE_GRID,
    PRODUCT_TILE_MEDIA,
    PRODUCT_TILE_SUBTITLE,
    PRODUCT_TILE_TITLE,
} from '../product-tile-grid';

/** Five rows of the 4-up grid — a run of the supplier's catalogue, not all of it. */
const MAX_TILES = 20;

/**
 * Two whole rows of the five-up grid, and two more each time the reader asks.
 * A multiple of the column count on purpose — revealing a part-row leaves a
 * ragged edge with more still to come. The last page may be short, but by then
 * there is nothing left to load and the button is gone.
 */
const PAGE_SIZE = 10;

/** Marks the tiles that share this product's range, inside the wider grid. */
const SameRangeBadge = () => (
    <span className="rounded-full bg-white/90 px-[0.625rem] py-[0.25rem] font-sans font-medium text-[#181818] backdrop-blur-sm max-md:text-[0.6875rem] md:text-[0.75rem]">
        Same range
    </span>
);

export const SupplierProductsRowContent = () => {
    const { product } = useLoaderData<typeof loader>();
    const { mobile } = useAppDisplayModeContext();
    const professional = product.Professional;
    const rangeTitle = product.LibraryRange?.Title;

    const { tiles, rangeIds } = useMemo(() => {
        const range = (product.ProductRange?.products ?? []) as DetailTile[];
        const supplier = (product.AlsoFromProducts?.products ??
            []) as DetailTile[];
        const ids = new Set(range.map((tile) => tile.id));
        const seen = new Set<number>([product.ID]);
        const merged: DetailTile[] = [];
        for (const tile of [...range, ...supplier]) {
            if (seen.has(tile.id)) continue;
            seen.add(tile.id);
            merged.push(tile);
        }
        return { tiles: merged.slice(0, MAX_TILES), rangeIds: ids };
    }, [product.ProductRange, product.AlsoFromProducts, product.ID]);

    const [shown, setShown] = useState(PAGE_SIZE);
    const visible = tiles.slice(0, shown);

    const adminData = useItemAdminData(visible.map((tile) => tile.id));

    const islandMounted = useMagicIslandController() !== null;
    const islandMode = useMemo<IslandMode>(
        () => ({
            kind: 'link',
            label: `See more from ${professional?.Title ?? 'this supplier'}`,
            to: professional?.Link ?? '',
        }),
        [professional?.Title, professional?.Link],
    );
    // Desktop only: on mobile the persistent Enquire pill stays put rather than
    // being swapped for an internal nav link that pulls the reader off the
    // product. Past half-scroll the island belongs to the enquiry fallback.
    const enquiryOwnsIsland = useEnquiryOwnsIsland();
    const islandRef = useIslandSection<HTMLDivElement>(islandMode, {
        enabled:
            !mobile &&
            islandMounted &&
            Boolean(professional?.Link) &&
            !enquiryOwnsIsland,
    });

    if (tiles.length === 0) return null;

    return (
        <div ref={islandRef} className="pb-[1.8rem] max-md:pb-4">
            <div className={PRODUCT_TILE_GRID}>
                {visible.map((tile) => (
                    <DetailTileCard
                        key={tile.id}
                        // The directory grids' phone treatment: the `•••`
                        // under the picture, beside the title.
                        mobileActionsInCaption
                        // Two legacy rails merged into one grid, so each tile
                        // still reports as the rail it came from: the range
                        // had its own event, the supplier's other products
                        // never did.
                        section={
                            rangeIds.has(tile.id)
                                ? 'ProductRange'
                                : 'ProductRelated'
                        }
                        tile={{
                            ...tile,
                            pinType: 'ProductPin',
                            adminData: adminData[tile.id],
                            professional: {
                                id: professional?.ID,
                                name: professional?.Title,
                            },
                            professionalLink: professional?.Link,
                            website: professional?.Website,
                        }}
                        badge={
                            rangeTitle && rangeIds.has(tile.id) ? (
                                <SameRangeBadge />
                            ) : undefined
                        }
                        mediaClassName={PRODUCT_TILE_MEDIA}
                        textClassName={PRODUCT_TILE_TITLE}
                        subtitleClassName={PRODUCT_TILE_SUBTITLE}
                    />
                ))}
            </div>

            {shown < tiles.length ? (
                <LoadMoreButton
                    className="max-md:mt-8 md:mt-[4rem]"
                    onClick={() =>
                        setShown((current) =>
                            Math.min(tiles.length, current + PAGE_SIZE),
                        )
                    }
                />
            ) : null}
        </div>
    );
};
